import { forwardRef, useCallback, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import SpellcheckContextMenuPopover from "@/components/SpellcheckContextMenuPopover";
import SpellcheckSuggestionBar from "@/components/SpellcheckSuggestionBar";
import { useSpellcheckContextMenu } from "@/hooks/useSpellcheckContextMenu";
import { isEmbeddedBrowserContext } from "@/lib/embeddedBrowserContext";
import { buildSpellcheckSegments } from "@/lib/spellcheckHighlight";

function SpellcheckMirror({ value, matches, className, mirrorRef }) {
  const segments = buildSpellcheckSegments(value, matches);
  return (
    <div
      ref={mirrorRef}
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words",
        "px-3 py-2 text-base md:text-sm text-slate-900",
        className,
      )}
      aria-hidden
    >
      {segments.map((seg, index) =>
        seg.type === "err" ? (
          <span
            key={`err-${index}`}
            className="underline decoration-red-500 decoration-wavy decoration-2 underline-offset-[3px]"
          >
            {seg.text}
          </span>
        ) : (
          <span key={`ok-${index}`}>{seg.text}</span>
        ),
      )}
    </div>
  );
}

/**
 * Área de texto con corrector: subrayado solo en palabras erróneas + chips + menú contextual.
 */
const SpellcheckTextarea = forwardRef(function SpellcheckTextarea({
  value,
  onChange,
  className,
  label,
  labelClassName,
  enableSpellcheck = true,
  spellCheck,
  lang = "es-MX",
  testId,
  wrapperClassName,
  minChars = 3,
  showSuggestionBar = true,
  ...props
}, ref) {
  const embedded = isEmbeddedBrowserContext();
  const mirrorRef = useRef(null);
  const innerRef = useRef(null);

  const setRefs = useCallback(
    (node) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  const onApply = useCallback((next) => {
    onChange?.({ target: { value: next } });
  }, [onChange]);

  const {
    matches,
    menu,
    handleContextMenu,
    handleMouseUp,
    applySuggestion,
    applyMatchSuggestion,
    ignoreMatchSuggestion,
    useNativeSpellcheck,
  } = useSpellcheckContextMenu({
    value,
    onApply,
    enabled: enableSpellcheck,
    minChars,
  });

  const resolvedSpellCheck = spellCheck ?? (enableSpellcheck && useNativeSpellcheck ? true : false);
  // El modo mirror pone text-transparent en el textarea. En Simple Browser / Cursor
  // preview el espejo no se pinta bien y el campo se ve vacío aunque haya valor.
  // Preferimos texto siempre visible; el menú contextual y la barra de chips siguen activos.
  const useMirror = false;

  const syncMirrorScroll = useCallback((event) => {
    const el = mirrorRef.current;
    if (!el) return;
    el.scrollTop = event.currentTarget.scrollTop;
    el.scrollLeft = event.currentTarget.scrollLeft;
  }, []);

  const fieldClassName = cn(
    "min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm",
    "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
    "disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
    className,
  );

  return (
    <div className={cn("space-y-1.5", wrapperClassName)} data-testid={testId ? `${testId}-wrap` : undefined}>
      {label ? <Label className={cn("text-xs", labelClassName)}>{label}</Label> : null}
      {useMirror ? (
        <div
          className={cn(
            "relative rounded-md border border-input bg-background shadow-sm focus-within:ring-1 focus-within:ring-ring",
            matches.length > 0 && "border-amber-200",
          )}
        >
          <SpellcheckMirror
            value={value}
            matches={matches}
            className={cn(className, "bg-transparent border-0 shadow-none mt-0")}
            mirrorRef={mirrorRef}
          />
          <textarea
            ref={setRefs}
            value={value}
            onChange={onChange}
            className={fieldClassName}
            spellCheck={false}
            lang={lang}
            autoCorrect="off"
            autoCapitalize="off"
            onContextMenu={handleContextMenu}
            onMouseUp={handleMouseUp}
            onScroll={syncMirrorScroll}
            title={embedded
              ? "Clic derecho o seleccione la palabra para ver sugerencias"
              : "Clic derecho sobre la palabra subrayada para ver sugerencias"}
            data-testid={testId}
            {...props}
          />
        </div>
      ) : (
        <Textarea
          ref={setRefs}
          value={value}
          onChange={onChange}
          className={className}
          spellCheck={resolvedSpellCheck}
          lang={enableSpellcheck ? lang : undefined}
          autoCorrect="off"
          autoCapitalize="off"
          onContextMenu={handleContextMenu}
          onMouseUp={handleMouseUp}
          title={enableSpellcheck
            ? (embedded
              ? "Clic derecho o seleccione la palabra para ver sugerencias"
              : "Clic derecho sobre la palabra subrayada para ver sugerencias")
            : undefined}
          data-testid={testId}
          {...props}
        />
      )}
      {enableSpellcheck && showSuggestionBar ? (
        <SpellcheckSuggestionBar
          value={value}
          matches={matches}
          onApply={applyMatchSuggestion}
          onIgnore={ignoreMatchSuggestion}
          testId={testId ? `${testId}-suggestions` : "spellcheck-suggestion-bar"}
        />
      ) : null}
      <SpellcheckContextMenuPopover
        open={Boolean(menu)}
        x={menu?.x ?? 0}
        y={menu?.y ?? 0}
        suggestions={menu?.suggestions ?? []}
        onSelect={applySuggestion}
        testId={testId ? `${testId}-spell-menu` : undefined}
      />
    </div>
  );
});

export default SpellcheckTextarea;
